import Model from '../../ModelBinding/Model';
import AddressCollection from '../Address/AddressCollection';
import OrganizationCollection from './OrganizationCollection';
import dayjs from 'dayjs';
import OrganizationPaymentInfo from '../OrganizationPaymentInfo/OrganizationPaymentInfo';
import OrganizationBillingInfo from '../OrganizationBillingInfo/OrganizationBillingInfo';
import OrganizationResolvedBillingInfo from '../OrganizationResolvedBillingInfo/OrganizationResolvedBillingInfo';
import InvitationCollection from '../Invitation/InvitationCollection';
import Member from '../Member/Member';
import MemberCollection from '../Member/MemberCollection';
import ServiceCollection from '../Service/ServiceCollection';
import VoucherCollection from '../Voucher/VoucherCollection';
import Role from '../Role/Role';
import OrganizationIdentificationCollection from '../OrganizationIdentification/OrganizationIdentificationCollection';
import Criticality from '../Criticality/Criticality';
import RoleCollection from '../Role/RoleCollection';
import OrganizationLogo from '../OrganizationLogo/OrganizationLogo';

export default class Organization extends Model {
  public name?: string;
  public short_id?: string;
  public country_code?: string;
  public parent_organization_id?: string;
  public vertical?: string;
  public logo?: OrganizationLogo;
  public url?: string;
  public description?: string;
  public created_at?: dayjs.Dayjs;
  public updated_at?: dayjs.Dayjs;
  public default_role_id?: number;
  public priority?: number;
  public enabled?: boolean;
  public missions_visibility?: boolean;
  public ready?: boolean;
  public mail_template_unsubscriptions?: string[];
  public mail_template_category_unsubscriptions?: string[];
  public identifications?: OrganizationIdentificationCollection;
  public payment_info?: OrganizationPaymentInfo;
  public billing_info?: OrganizationBillingInfo;
  public resolved_billing_info?: OrganizationResolvedBillingInfo;
  public addresses?: AddressCollection;
  public invitations?: InvitationCollection;
  public members?: MemberCollection;
  public organizations?: OrganizationCollection;
  public services?: ServiceCollection;
  public vouchers?: VoucherCollection;
  public relations?: MemberCollection;
  public role?: Role | string;
  public roles?: RoleCollection;
  public parent?: Organization;
  public criticality?: Criticality;
  public owner?: Member;
  public next_available_appointment?: string;

  bind() {
    this.name = this.getStringAttribute('name');
    this.short_id = this.getStringAttribute('short_id');
    this.country_code = this.getStringAttribute('country_code');
    this.parent_organization_id = this.getStringAttribute('parent_organization_id');
    this.vertical = this.getStringAttribute('vertical');
    this.logo = this.getRelation(
      'logo',
      OrganizationLogo,
    ) as OrganizationLogo;
    this.url = this.getStringAttribute('url');
    this.description = this.getStringAttribute('description');
    this.created_at = this.getDateAttribute('created_at');
    this.updated_at = this.getDateAttribute('updated_at');
    this.default_role_id = this.getIntAttribute('default_role_id');
    this.priority = this.getIntAttribute('priority');
    this.enabled = this.getBooleanAttribute('enabled');
    this.missions_visibility = this.getBooleanAttribute('missions_visibility');
    this.ready = this.getBooleanAttribute('ready');
    this.mail_template_unsubscriptions = this.getOriginalAttribute('mail_template_unsubscriptions');
    this.mail_template_category_unsubscriptions = this.getOriginalAttribute('mail_template_category_unsubscriptions');
    this.identifications = this.getRelationAttributeCollection(
      'identifications',
      OrganizationIdentificationCollection,
    ) as OrganizationIdentificationCollection;
    this.payment_info = this.getRelationAttribute('payment_info', OrganizationPaymentInfo) as
      | OrganizationPaymentInfo
      | undefined;
    this.billing_info = this.getRelationAttribute('billing_info', OrganizationBillingInfo) as
      | OrganizationBillingInfo
      | undefined;
    this.resolved_billing_info = this.getRelationAttribute('resolved_billing_info', OrganizationResolvedBillingInfo) as
      | OrganizationResolvedBillingInfo
      | undefined;
    this.addresses = this.getRelationCollection(
      'addresses',
      AddressCollection,
    ) as AddressCollection;
    this.invitations = this.getRelationCollection(
      'invitations',
      InvitationCollection,
    ) as InvitationCollection;
    this.members = this.getRelationCollection('members', MemberCollection) as MemberCollection;
    this.organizations = this.getRelationCollection(
      'organizations',
      OrganizationCollection,
    ) as OrganizationCollection;
    this.services = this.getRelationCollection('services', ServiceCollection) as ServiceCollection;
    this.vouchers = this.getRelationCollection('vouchers', VoucherCollection) as VoucherCollection;
    this.relations = this.getRelationCollection('relations', MemberCollection) as MemberCollection;
    this.role =
      this.getStringAttribute('role') || (this.getRelation('role', Role) as Role | undefined);
    this.roles = this.getRelationCollection('roles', RoleCollection) as RoleCollection;
    this.parent = this.getRelation('parent', Organization) as Organization | undefined;
    this.criticality = this.getRelation('criticality', Criticality) as Criticality | undefined;
    this.owner = this.getRelation('owner', Member ) as Member | undefined;
    this.next_available_appointment = this.getStringAttribute('next_available_appointment');
  }

  static getCollection() {
    return OrganizationCollection;
  }

  toString() {
    return this.name;
  }
}
